import { useState } from "react";
import PropTypes from "prop-types";
import { BbotButton, BbotModal, BbotTable, SettingsCard } from "top-component-library";
import { formatScheduleSummary } from "../orderRateLimitUtils";
import OrderRateLimitDialog from "../OrderRateLimitDialog";

const stopRowClick = (event) => event.stopPropagation();

const COLUMNS = [
  { title: "Name", dataIndex: "name_for_owner", key: "name_for_owner" },
  { title: "Date Range", dataIndex: "date_range_display", key: "date_range_display" },
  {
    title: "Times",
    key: "active_schedule",
    render: (_value, row) => formatScheduleSummary(row.active_schedule).map((line) => <div key={line}>{line}</div>),
  },
  {
    title: "Fulfillment Method",
    key: "fulfillment_methods",
    render: (_value, row) => row.fulfillment_methods.join(", "),
  },
  { title: "Locations", key: "location_ids", render: (_value, row) => row.location_ids.length },
];

const CheckoutLimitationsCard = ({ orderRateLimits, availableThrottleSettings, customerId, onSave, onDelete }) => {
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);

  const openAdd = () => {
    setEditingConfig(null);
    setDialogVisible(true);
  };

  const openEdit = (config) => {
    setEditingConfig(config);
    setDialogVisible(true);
  };

  const closeDialog = () => {
    setDialogVisible(false);
    setEditingConfig(null);
  };

  const confirmDelete = async () => {
    const success = await onDelete(pendingDeleteIds);
    if (success) setPendingDeleteIds([]);
  };

  const columns = [
    ...COLUMNS,
    {
      title: "Edit",
      key: "edit",
      render: (_value, row) => (
        <span onClick={stopRowClick}>
          <BbotButton type="link" onClick={() => openEdit(row)}>
            Edit
          </BbotButton>
        </span>
      ),
    },
  ];

  const dialog = (
    <OrderRateLimitDialog
      visible={dialogVisible}
      config={editingConfig}
      customerId={customerId}
      availableThrottleSettings={availableThrottleSettings}
      onClose={closeDialog}
      onSave={onSave}
    />
  );

  if (orderRateLimits.length === 0) {
    return (
      <SettingsCard title="Add Checkout Limitations">
        <p>
          You can limit the number of orders allowed per time period, to prevent overloading your kitchen on busy
          nights. If too many orders are scheduled for a peak time, further orders will not be allowed for that time.
        </p>
        <p>
          Note: This does not affect ASAP orders. To use this setting, you will need to disable ASAP orders by setting
          the &quot;Shortest delivery/pickup time&quot; on each of your menus to something greater than 0 minutes.
        </p>
        <p>
          You can also use this feature to limit the number of tagged menu items in an order, or make ordering menu
          items with certain tags prerequisite on ordering others with different tags.
        </p>
        <BbotButton type="primary" onClick={openAdd}>
          Enable
        </BbotButton>
        {dialog}
      </SettingsCard>
    );
  }

  return (
    <SettingsCard title="Checkout Limitations">
      <p>
        You can limit the number of orders allowed per time period, to prevent overloading your kitchen on busy nights.
        If too many orders are scheduled for a peak time, further orders will not be allowed for that time. Note: This
        does not affect ASAP orders.
      </p>
      <BbotTable
        id="order-rate-limits"
        titleRight={
          <BbotButton type="primary" onClick={openAdd}>
            Add Checkout Limitation
          </BbotButton>
        }
        manuallyFormattedColumns={columns}
        data={orderRateLimits.map((limit) => ({ ...limit, key: limit.id }))}
        interactive
        allowBulkDeletion
        onBulkDelete={(ids) => setPendingDeleteIds(ids)}
      />
      <BbotModal
        title="Are you sure you'd like to delete these throttling settings?"
        visible={pendingDeleteIds.length > 0}
        onCancel={() => setPendingDeleteIds([])}
        okText="Delete"
        okType="danger"
        onOk={confirmDelete}
      >
        You are about to delete {pendingDeleteIds.length} throttling setting{pendingDeleteIds.length > 1 ? "s" : ""}.
      </BbotModal>
      {dialog}
    </SettingsCard>
  );
};

CheckoutLimitationsCard.propTypes = {
  orderRateLimits: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })).isRequired,
  availableThrottleSettings: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string.isRequired, display_name: PropTypes.string.isRequired })
  ).isRequired,
  customerId: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default CheckoutLimitationsCard;
